import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, LinearProgress, Box, Chip,
  Snackbar
} from '@material-ui/core'
import {
  Description as PlansIcon,
  PhotoLibrary as PhotosIcon,
  Receipt as QuoteIcon,
  AttachMoney as InvoiceIcon,
  InsertDriveFile as FilesIcon,
  CheckCircle as SignedIcon,
  CloudDone as DoneIcon
} from '@material-ui/icons'
import Alert from '@material-ui/lab/Alert'
import axios from 'axios'

const categories = [
  { key: 'plans', label: 'Plans', icon: PlansIcon, color: '#1976d2' },
  { key: 'photos', label: 'Photos', icon: PhotosIcon, color: '#7b1fa2' },
  { key: 'vendor_quote', label: 'Vendor Quote', icon: QuoteIcon, color: '#2e7d32' },
  { key: 'vendor_invoice', label: 'Vendor Invoice', icon: InvoiceIcon, color: '#e65100' },
  { key: 'files', label: 'Files', icon: FilesIcon, color: '#546e7a' },
  { key: 'signed_estimate', label: 'Signed Estimate', icon: SignedIcon, color: '#00838f' }
]

const categoryChipStyle = (color, selected) => ({
  margin: 4,
  padding: '20px 12px',
  fontSize: 14,
  fontWeight: selected ? 700 : 400,
  cursor: 'pointer',
  backgroundColor: selected ? color : '#f5f5f5',
  color: selected ? '#fff' : '#333',
  border: selected ? 'none' : '1px solid #ddd',
  transition: 'all 0.15s ease'
})

const STEPS = {
  UPLOADING: 'uploading',
  CATEGORY: 'category',
  PLANS_FOLLOWUP: 'plans_followup',
  DONE: 'done'
}

export default function UploadCategoryWizard ({ file, projectId, url, onClose, onComplete }) {
  const [step, setStep] = useState(STEPS.UPLOADING)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadResult, setUploadResult] = useState(null)
  const [error, setError] = useState(null)
  const [snack, setSnack] = useState(null)

  // Start upload immediately on mount
  React.useEffect(() => {
    uploadFile()
  }, [])

  const uploadFile = async () => {
    setStep(STEPS.UPLOADING)
    setError(null)

    try {
      // First upload without category — we'll set it after the user picks
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', 'files') // Default, will be updated

      const { data } = await axios.post(
        `${url || ''}/api/bid-estimation/projects/${projectId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          onUploadProgress: (e) => {
            if (e.total) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100))
            }
          }
        }
      )
      setUploadResult(data)
      setStep(STEPS.CATEGORY)
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed')
      setStep(STEPS.CATEGORY) // Still show category picker so user can retry
    }
  }

  const handleCategorySelect = async (categoryKey) => {
    setSelectedCategory(categoryKey)
  }

  const handleConfirmCategory = async () => {
    if (!selectedCategory) return

    // If we had an upload error, re-upload with the selected category
    if (error || !uploadResult) {
      setStep(STEPS.UPLOADING)
      setError(null)
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', selectedCategory)

        const { data } = await axios.post(
          `${url || ''}/api/bid-estimation/projects/${projectId}/upload`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
            onUploadProgress: (e) => {
              if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
            }
          }
        )
        setUploadResult(data)
        setError(null)
      } catch (e) {
        setError(e.response?.data?.error || 'Upload failed')
        return
      }
    }

    // Handle category-specific follow-ups
    if (selectedCategory === 'plans') {
      setStep(STEPS.PLANS_FOLLOWUP)
      return
    }

    if (selectedCategory === 'vendor_quote') {
      // If the upload already triggered a parse (PDF), pass the result through
      if (uploadResult?.vendorQuote) {
        onComplete({ ...uploadResult, category: selectedCategory, action: 'open_quote_review' })
        return
      }
    }

    // All others — done
    setStep(STEPS.DONE)
    setTimeout(() => {
      onComplete({ ...uploadResult, category: selectedCategory })
    }, 1000)
  }

  const handleTakeoffRequest = (wantsTakeoff) => {
    if (wantsTakeoff) {
      setSnack({ severity: 'info', message: 'Takeoff estimation requests coming soon!' })
    }
    setStep(STEPS.DONE)
    setTimeout(() => {
      onComplete({ ...uploadResult, category: selectedCategory, takeoffRequested: wantsTakeoff })
    }, 1000)
  }

  const fileSizeMB = file ? (file.size / 1024 / 1024).toFixed(2) : '0'

  return (
    <>
      <Dialog open maxWidth="sm" fullWidth onClose={step === STEPS.UPLOADING ? undefined : onClose}>
        {/* ── Uploading Step ─────────────────────────────── */}
        {step === STEPS.UPLOADING && (
          <>
            <DialogTitle>Uploading...</DialogTitle>
            <DialogContent>
              <Box style={{ textAlign: 'center', padding: '20px 0' }}>
                <Typography variant="body1" style={{ marginBottom: 8 }}>{file.name}</Typography>
                <Typography variant="caption" color="textSecondary">{fileSizeMB} MB</Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  style={{ marginTop: 16, height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                  {uploadProgress}%
                </Typography>
              </Box>
            </DialogContent>
          </>
        )}

        {/* ── Category Picker Step ───────────────────────── */}
        {step === STEPS.CATEGORY && (
          <>
            <DialogTitle>What type of file is this?</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>
              )}
              <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                <Typography variant="body2" color="textSecondary">
                  {file.name} ({fileSizeMB} MB)
                </Typography>
              </Box>
              <Box style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, padding: '8px 0' }}>
                {categories.map(cat => {
                  const Icon = cat.icon
                  return (
                    <Chip
                      key={cat.key}
                      icon={<Icon style={{ color: selectedCategory === cat.key ? '#fff' : cat.color }} />}
                      label={cat.label}
                      onClick={() => handleCategorySelect(cat.key)}
                      style={categoryChipStyle(cat.color, selectedCategory === cat.key)}
                    />
                  )
                })}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleConfirmCategory}
                color="primary"
                variant="contained"
                disabled={!selectedCategory}
              >
                Continue
              </Button>
            </DialogActions>
          </>
        )}

        {/* ── Plans Follow-up Step ───────────────────────── */}
        {step === STEPS.PLANS_FOLLOWUP && (
          <>
            <DialogTitle>Plans Uploaded</DialogTitle>
            <DialogContent>
              <Box style={{ textAlign: 'center', padding: '20px 0' }}>
                <DoneIcon style={{ fontSize: 48, color: '#2e7d32', marginBottom: 12 }} />
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  {file.name} saved as Plans
                </Typography>
                <Typography variant="body1" style={{ marginBottom: 8 }}>
                  Do you wish to request Takeoff Estimates?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  We can analyze the plans and generate material/labor takeoff estimates.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleTakeoffRequest(false)}>
                No, I'm Done
              </Button>
              <Button
                onClick={() => handleTakeoffRequest(true)}
                color="primary"
                variant="contained"
              >
                Yes, Request Takeoff
              </Button>
            </DialogActions>
          </>
        )}

        {/* ── Done Step ──────────────────────────────────── */}
        {step === STEPS.DONE && (
          <>
            <DialogContent>
              <Box style={{ textAlign: 'center', padding: '40px 0' }}>
                <DoneIcon style={{ fontSize: 64, color: '#2e7d32' }} />
                <Typography variant="h6" style={{ marginTop: 12 }}>
                  File Uploaded Successfully
                </Typography>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
      >
        {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
      </Snackbar>
    </>
  )
}
